<template>
    <div class="page">
        <div class="page-head">
            <div>
                <h1 class="page-title">Users</h1>
                <p class="page-sub">Subscriptions, approvals and revenue at a glance.</p>
            </div>
        </div>

        <div class="tiles">
            <div v-for="stat in stats.slice(0, 4)" :key="stat.title" class="tile" :class="{ danger: stat.danger && Number(stat.value) > 0 }">
                <div class="tile-label">
                    <span>{{ stat.title }}</span>
                    <component :is="icons[stat.icon]" />
                </div>
                <div class="tile-value">{{ format(stat) }}</div>
            </div>
        </div>

        <div class="tiles three">
            <div v-for="stat in stats.slice(4, 7)" :key="stat.title" class="tile" :class="{ gold: stat.gold }">
                <div class="tile-label">
                    <span>{{ stat.title }}</span>
                    <component :is="icons[stat.icon]" />
                </div>
                <div class="tile-value">{{ format(stat) }}</div>
            </div>
        </div>

        <a-card title="Recent users" :bordered="false" class="user-table-card">
            <UserTable />
        </a-card>
    </div>
</template>

<script>
import {
    UserOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    UserSwitchOutlined,
    PayCircleOutlined,
    DollarOutlined,
    ClockCircleOutlined
} from '@ant-design/icons-vue';
import UserTable from '@/components/UserTable.vue';
import { collection, getCountFromServer, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '@/firebase/config';

export default {
    components: {
        UserTable,
        UserOutlined,
        TeamOutlined,
        CheckCircleOutlined,
        CloseCircleOutlined,
        UserSwitchOutlined,
        PayCircleOutlined,
        DollarOutlined,
        ClockCircleOutlined
    },
    data() {
        return {
            users: [],
            icons: {
                UserOutlined,
                TeamOutlined,
                CheckCircleOutlined,
                CloseCircleOutlined,
                UserSwitchOutlined,
                PayCircleOutlined,
                DollarOutlined,
                ClockCircleOutlined
            },
            stats: [
                {
                    title: 'Total users',
                    value: 0,
                    icon: 'TeamOutlined',
                    precision: 0
                },
                {
                    title: 'Subscribers',
                    value: 0,
                    icon: 'UserOutlined',
                    precision: 0
                },
                {
                    title: 'Active subscriptions',
                    value: 0,
                    icon: 'CheckCircleOutlined',
                    precision: 0
                },
                {
                    title: 'Suspended users',
                    value: 0,
                    icon: 'CloseCircleOutlined',
                    danger: true,
                    precision: 0
                },
                {
                    title: 'Affiliates',
                    value: 0,
                    icon: 'UserSwitchOutlined',
                    precision: 0
                },
                {
                    title: 'Revenue (RMB)',
                    value: 0,
                    icon: 'PayCircleOutlined',
                    gold: true,
                    prefix: '¥',
                    precision: 2
                },
                {
                    title: 'Revenue (USD)',
                    value: 0,
                    icon: 'DollarOutlined',
                    prefix: '$',
                    precision: 2
                }
            ],
        };
    },
    async created() {
        // Get total users count efficiently
        const getTotalUsers = async () => {
            const usersCollection = collection(db, 'users');
            const snapshot = await getCountFromServer(usersCollection);
            return snapshot.data().count;
        }

        // Get affiliates count
        const getAffiliates = async () => {
            const affiliatesCollection = collection(db, 'affiliates');
            const snapshot = await getCountFromServer(affiliatesCollection);
            return snapshot.data().count;
        }

        // Get total subscribers count (users with subscription data)
        const getTotalSubscribers = async () => {
            const usersCollection = collection(db, 'users');
            const q = query(usersCollection, where('subscriptionData', '!=', null));
            const snapshot = await getCountFromServer(q);
            return snapshot.data().count;
        }

        // Get active subscriptions count
        const getActiveSubscriptions = async () => {
            const usersCollection = collection(db, 'users');
            const q = query(usersCollection, where('subscriptionStatus', '==', 'active'));

            const snapshot = await getCountFromServer(q);
            return snapshot.data().count;
        }

        // Get suspended users count
        const getSuspendedUsers = async () => {
            const usersCollection = collection(db, 'users');
            const q = query(usersCollection, where('profileStatus', '==', 'suspended'));

            const snapshot = await getCountFromServer(q);
            return snapshot.data().count;
        }

        // Get revenue
        const getRevenue = async () => {
            const docRef = doc(db, 'service-data', 'stats');
            const revSnap = await getDoc(docRef);
            return revSnap.exists() ? revSnap.data().revenue : 0;
        }

        // Load everything at once; one failing count must not blank the others
        const results = await Promise.allSettled([
            getTotalUsers(), getTotalSubscribers(), getActiveSubscriptions(), getSuspendedUsers(), getAffiliates(), getRevenue()
        ]);
        const [total, subscribers, active, suspended, affiliates, revenue] = results.map((r) => (r.status === 'fulfilled' ? r.value : 0));
        results.forEach((r) => r.status === 'rejected' && console.warn('Dashboard stat failed:', r.reason));
        this.stats[0].value = total;
        this.stats[1].value = subscribers;
        this.stats[2].value = active;
        this.stats[3].value = suspended;
        this.stats[4].value = affiliates;
        this.stats[5].value = revenue;
        this.stats[6].value = Number(revenue / 7.2).toFixed(2); // Convert RMB to USD
    },
    methods: {
        format(stat) {
            const n = Number(stat.value) || 0;
            const text = n.toLocaleString('en-US', { minimumFractionDigits: stat.precision, maximumFractionDigits: stat.precision });
            return `${stat.prefix || ''}${text}`;
        }
    }
};
</script>

<style>
.user-table-card .ant-card-body { padding-top: 16px; }
</style>
